/**
 * Parent "Analysis" accordion that groups the three deterministic
 * sub-sections (Cross-References, Word Study, Textual Variants)
 * behind a single outer toggle. Each child keeps its own inner
 * twister; this just wraps them in a subtle bordered container.
 */

import { useState } from 'react';
import type { CrossRef, ScriptureWord, TextualVariant } from '@skjv/common';
import { ChevronRightIcon } from '@components/icons';
import { CrossRefsSection } from './cross-refs-section';
import { StrongsWordList } from './strongs-word-list';
import { VariantSection } from './variant-section';

interface AnalysisSectionProps {
  crossRefs: CrossRef[];
  words: ScriptureWord[];
  variants: TextualVariant[];
}

export function AnalysisSection({ crossRefs, words, variants }: AnalysisSectionProps) {
  const [open, setOpen] = useState(false);

  const hasAny =
    crossRefs.length > 0 || words.some((w) => w.strongsNumbers.length > 0) || variants.length > 0;
  if (!hasAny) return null;

  return (
    <div className="analysis-section">
      <button
        type="button"
        className="analysis-section-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <ChevronRightIcon
          className={`h-3.5 w-3.5 analysis-section-chevron ${open ? 'analysis-section-chevron-open' : ''}`}
        />
        <span>Analysis</span>
      </button>
      {open && (
        <div className="analysis-section-body">
          <CrossRefsSection crossRefs={crossRefs} />
          <StrongsWordList words={words} />
          {variants.length > 0 && <VariantSection variants={variants} />}
        </div>
      )}
    </div>
  );
}
